import * as React from 'react';
import { XIcon } from 'lucide-react';
import { Dialog as DialogPrimitive } from 'radix-ui';

import { cn } from '@/lib/utils';

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

/**
 * §4: `rgba(11,11,12,.32)`, no blur.
 *
 * `forwardRef` is load-bearing on React 18: Radix wraps every child of `Dialog.Portal` in
 * `asChild`, so the Slot hands this component a ref. Without it React warns and `Presence`
 * never sees the node it needs to hold the overlay through its exit animation.
 */
const DialogOverlay = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(function DialogOverlay({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      data-slot="dialog-overlay"
      className={cn(
        'fixed inset-0 z-50 bg-overlay duration-(--animate-duration-dialog) ease-(--ease-standard) data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
        className,
      )}
      {...props}
    />
  );
});

/** §6: radius 16, lg shadow, 240ms fade + 8px rise. */
function DialogContent({
  className,
  children,
  showCloseButton = true,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
}) {
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          'fixed top-[50%] left-[50%] z-50 flex w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-lg outline-none sm:max-w-lg',
          'duration-(--animate-duration-dialog) ease-(--ease-standard) data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-bottom-2',
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            className="absolute top-5 right-5 rounded-md p-1 text-ink-2 transition-colors duration-(--animate-duration-hover) ease-(--ease-standard) hover:bg-canvas hover:text-ink focus-visible:ring-focus disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="dialog-header" className={cn('flex flex-col gap-2 px-6 pt-6', className)} {...props} />;
}

function DialogBody({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="dialog-body" className={cn('px-6 py-5', className)} {...props} />;
}

/**
 * §6: the footer sits on `--surface-1` above a 1px `--divider` rule, actions right-aligned.
 *
 * `leading` fills the empty left half with something that belongs to the commit rather than
 * to the body — SignModal's "Save signature" toggle is the case it was added for. Actions
 * stay pinned right whether or not it is supplied, so the default layout is unchanged.
 */
function DialogFooter({
  className,
  children,
  leading,
  ...props
}: React.ComponentProps<'div'> & { leading?: React.ReactNode }) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        'mt-auto flex items-center justify-between gap-3 border-t border-divider bg-surface-1 px-6 py-4',
        className,
      )}
      {...props}
    >
      {leading ? <div className="flex min-w-0 items-center">{leading}</div> : null}
      <div className="ml-auto flex items-center gap-2">{children}</div>
    </div>
  );
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return <DialogPrimitive.Title data-slot="dialog-title" className={cn('pr-8 text-h3-dialog', className)} {...props} />;
}

function DialogDescription({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
